import type { Theme } from '@connected/ui/store/theme';

import { useTheme } from '@connected/ui/hooks/use-theme';
import { useMatches } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

declare module '@tanstack/react-router' {
    // eslint-disable-next-line ts/consistent-type-definitions
    interface StaticDataRouteOption {
        theme?: Theme;
    }
}

/**
 * ThemeManager component that reads route metadata and applies themes accordingly.
 * This component listens to route changes and applies the theme specified in route's staticData.
 */
export function ThemeManager() {
    const matches = useMatches();
    const { setTheme } = useTheme();
    const defaultThemeRef = useRef<Theme>('default');
    const lastAppliedThemeRef = useRef<Theme | null>(null);

    useEffect(() => {
        // Find the most specific route that has a theme defined in staticData
        // Reverse the matches to prioritize the most specific (deepest) route
        const routeWithTheme = [...matches].reverse().find(
            match => (match.staticData)?.theme,
        );

        const routeTheme = (routeWithTheme?.staticData)?.theme || defaultThemeRef.current;

        // Only update if the theme actually changed
        if (routeTheme !== lastAppliedThemeRef.current) {
            lastAppliedThemeRef.current = routeTheme;
            setTheme(routeTheme);
        }
    }, [matches, setTheme]);

    return null; // This component doesn't render anything
}
